import React, { useState, useCallback } from 'react';
import type { Settings } from '../types';
import { useTask } from '../contexts/TaskContext.tsx';
import { formatDuration } from '../utils/formatDuration.ts';
import './TaskPanel.css';

interface TaskPanelProps {
  isCollapsed: boolean;
  onToggle: () => void;
  currentTaskElapsedSeconds?: number;
  settings: Settings;
  onSettingsChange: (settings: Settings) => void;
}

const TaskPanel: React.FC<TaskPanelProps> = ({ isCollapsed: isPanelCollapsed, onToggle, currentTaskElapsedSeconds, settings, onSettingsChange }) => {
  const {
    projects,
    tasks,
    currentTaskId,
    createProject,
    updateProject,
    deleteProject,
    createTask,
    updateTask,
    deleteTask,
    setCurrentTaskId,
    completeTask,
    uncompleteTask,
  } = useTask();

  const [editingTaskForProject, setEditingTaskForProject] = useState<string | null>(null);
  const [editingStandaloneTask, setEditingStandaloneTask] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renamingTaskId, setRenamingTaskId] = useState<string | null>(null);
  const [newTaskText, setNewTaskText] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [projectError, setProjectError] = useState('');
  const [taskError, setTaskError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAddTask = (projectId?: string) => {
    const trimmedText = newTaskText.trim();
    if (!trimmedText) { setTaskError('Task title is required'); return; }
    setTaskError('');
    createTask(trimmedText, projectId);
    setNewTaskText('');
    setEditingTaskForProject(null);
    setEditingStandaloneTask(false);
    const projectName = projectId ? projects.find(p => p.id === projectId)?.name : undefined;
    showSuccess(`Task "${trimmedText}" created${projectName ? ` in ${projectName}` : ''}`);
  };

  const handleAddProject = () => {
    const trimmedName = newProjectName.trim();
    if (!trimmedName) { setProjectError('Project name is required'); return; }
    if (projects.some(p => p.name.toLowerCase() === trimmedName.toLowerCase())) {
      setProjectError('A project with this name already exists'); return;
    }
    setProjectError('');
    createProject(trimmedName);
    setNewProjectName('');
    setEditingProject(false);
    showSuccess(`Project "${trimmedName}" created`);
  };

  const handleTaskKeyDown = (e: React.KeyboardEvent, projectId?: string) => {
    if (e.key === 'Enter') handleAddTask(projectId);
    else if (e.key === 'Escape') { setEditingTaskForProject(null); setEditingStandaloneTask(false); setNewTaskText(''); setTaskError(''); }
  };

  const handleProjectKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAddProject();
    else if (e.key === 'Escape') { setEditingProject(false); setNewProjectName(''); setProjectError(''); }
  };

  const startRenameProject = (id: string, currentName: string) => {
    setRenamingProjectId(id);
    setRenameValue(currentName);
  };

  const commitRenameProject = (id: string) => {
    const trimmed = renameValue.trim();
    if (trimmed && !projects.some(p => p.id !== id && p.name.toLowerCase() === trimmed.toLowerCase())) {
      updateProject(id, { name: trimmed });
    }
    setRenamingProjectId(null);
    setRenameValue('');
  };

  const startRenameTask = (id: string, currentText: string) => {
    setRenamingTaskId(id);
    setRenameValue(currentText);
  };

  const commitRenameTask = (id: string) => {
    const trimmed = renameValue.trim();
    if (trimmed) updateTask(id, { text: trimmed });
    setRenamingTaskId(null);
    setRenameValue('');
  };

  const handleDeleteTask = (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    deleteTask(taskId);
    if (task) showSuccess(`Task "${task.text}" deleted`);
  };

  const handleDeleteProject = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    const taskCount = tasks.filter(t => t.projectId === projectId).length;
    // If deleted project contained the current task, clear it
    if (currentTaskId && tasks.find(t => t.id === currentTaskId && t.projectId === projectId)) {
      setCurrentTaskId(null);
    }
    deleteProject(projectId);
    if (project) showSuccess(`Project "${project.name}" and ${taskCount} task${taskCount !== 1 ? 's' : ''} deleted`);
  };

  const handleTaskClick = useCallback((taskId: string) => {
    setCurrentTaskId(currentTaskId === taskId ? null : taskId);
  }, [currentTaskId, setCurrentTaskId]);

  const showCompleted = settings.showCompletedTasks;

  const getTasksByProject = (projectId: string) =>
    tasks.filter(t => t.projectId === projectId && (showCompleted || !t.completed));
  const getStandaloneTasks = () =>
    tasks.filter(t => !t.projectId && (showCompleted || !t.completed));
  const getCurrentTaskName = () => tasks.find(t => t.id === currentTaskId)?.text ?? null;

  const getDisplayTime = (taskId: string, base: number) =>
    taskId === currentTaskId && currentTaskElapsedSeconds ? base + currentTaskElapsedSeconds : base;

  const handleToggleComplete = useCallback((e: React.MouseEvent, taskId: string, isCompleted: boolean) => {
    e.stopPropagation();
    if (isCompleted) uncompleteTask(taskId);
    else completeTask(taskId);
  }, [completeTask, uncompleteTask]);

  return (
    <div className={`task-panel ${isPanelCollapsed ? 'collapsed' : 'expanded'}`}>
      <div className="task-header">
        <button className="panel-toggle" onClick={onToggle}>
          {isPanelCollapsed ? '▶' : '◀'}
        </button>
        {!isPanelCollapsed && (
          <>
            <h2 className="task-title">Tasks <span className="task-count">{tasks.length}</span></h2>
            <button
              className={`show-completed-toggle ${showCompleted ? 'active' : ''}`}
              onClick={() => onSettingsChange({ ...settings, showCompletedTasks: !showCompleted })}
              title={showCompleted ? 'Hide completed tasks' : 'Show completed tasks'}
            >
              {showCompleted ? '◉' : '○'} Done
            </button>
          </>
        )}
      </div>

      {isPanelCollapsed && (
        <div className="vertical-text" onClick={onToggle}>
          Projects and Tasks
        </div>
      )}

      {!isPanelCollapsed && (
        <>
          {successMessage && <div className="success-message">✓ {successMessage}</div>}

          <div className="current-task-section">
            <div className="current-task-header">
              <span className="current-task-label">Current Task:</span>
              {currentTaskId ? (
                <div className="current-task-display">
                  <span className="current-task-name">{getCurrentTaskName()}</span>
                  <button className="clear-task-btn" onClick={() => setCurrentTaskId(null)} title="Clear current task">×</button>
                </div>
              ) : (
                <span className="no-task-selected">No task selected</span>
              )}
            </div>
          </div>

          <div className="hierarchy-header">
            {!editingProject ? (
              <button className="add-project-btn" onClick={() => setEditingProject(true)}>+ Add Project</button>
            ) : (
              <div className="inline-project-input">
                <div className="project-input-container">
                  <input
                    type="text"
                    className={`project-input ${projectError ? 'error' : ''}`}
                    placeholder="Enter project name. ENTER to save"
                    value={newProjectName}
                    onChange={(e) => { setNewProjectName(e.target.value); if (projectError) setProjectError(''); }}
                    onKeyDown={handleProjectKeyDown}
                    autoFocus
                  />
                  <button className="project-input-cancel" onClick={() => { setEditingProject(false); setNewProjectName(''); setProjectError(''); }}>×</button>
                </div>
                {projectError && <div className="error-message">{projectError}</div>}
              </div>
            )}
          </div>

          <div className="task-list">
            {projects.map(project => (
              <div key={project.id} className="project-section">
                <div className="project-header">
                  <button className="project-toggle" onClick={() => updateProject(project.id, { collapsed: !project.collapsed })}>
                    {project.collapsed ? '▶' : '▼'}
                  </button>
                  {renamingProjectId === project.id ? (
                    <input
                      className="inline-rename-input"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => commitRenameProject(project.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRenameProject(project.id);
                        if (e.key === 'Escape') { setRenamingProjectId(null); setRenameValue(''); }
                      }}
                      autoFocus
                    />
                  ) : (
                    <span className="project-name" onDoubleClick={() => startRenameProject(project.id, project.name)}>
                      {project.name}
                    </span>
                  )}
                  <span className="project-task-count">{getTasksByProject(project.id).length}</span>
                  {!project.collapsed && (
                    <button className="add-task-btn" onClick={() => { setEditingTaskForProject(project.id); setEditingStandaloneTask(false); }}>+</button>
                  )}
                  <button className="project-delete" onClick={() => handleDeleteProject(project.id)}>×</button>
                </div>

                {!project.collapsed && (
                  <div className="project-tasks">
                    {getTasksByProject(project.id).map(task => (
                      <div
                        key={task.id}
                        className={`task-item project-task ${currentTaskId === task.id ? 'selected' : ''} ${task.completed ? 'completed' : ''}`}
                        onClick={() => !task.completed && handleTaskClick(task.id)}
                      >
                        <button
                          className={`complete-btn ${task.completed ? 'is-complete' : ''}`}
                          onClick={e => handleToggleComplete(e, task.id, task.completed)}
                          title={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
                        >
                          {task.completed ? '✓' : ''}
                        </button>
                        <div className="task-content">
                          {renamingTaskId === task.id ? (
                            <input
                              className="inline-rename-input"
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onBlur={() => commitRenameTask(task.id)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') commitRenameTask(task.id);
                                if (e.key === 'Escape') { setRenamingTaskId(null); setRenameValue(''); }
                              }}
                              onClick={e => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <span className="task-text" onDoubleClick={e => { e.stopPropagation(); startRenameTask(task.id, task.text); }}>{task.text}</span>
                          )}
                          <span className="task-time">{formatDuration(getDisplayTime(task.id, task.totalTimeSpent))}</span>
                        </div>
                        <button className="task-delete" onClick={e => { e.stopPropagation(); handleDeleteTask(task.id); }}>×</button>
                      </div>
                    ))}
                    {editingTaskForProject === project.id && (
                      <div className="inline-task-input">
                        <div className="task-input-container">
                          <input
                            type="text"
                            className={`task-input ${taskError ? 'error' : ''}`}
                            placeholder="Enter task name. ENTER to save."
                            value={newTaskText}
                            onChange={e => { setNewTaskText(e.target.value); if (taskError) setTaskError(''); }}
                            onKeyDown={e => handleTaskKeyDown(e, project.id)}
                            autoFocus
                          />
                          <button className="task-input-cancel" onClick={() => { setEditingTaskForProject(null); setNewTaskText(''); setTaskError(''); }}>×</button>
                        </div>
                        {taskError && <div className="error-message">{taskError}</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Standalone tasks */}
            {(getStandaloneTasks().length > 0 || editingStandaloneTask) && (
              <div className="standalone-tasks">
                <div className="standalone-header">
                  <span className="standalone-title">Standalone Tasks</span>
                  <span className="standalone-task-count">{getStandaloneTasks().length}</span>
                  <button className="add-task-btn" onClick={() => { setEditingStandaloneTask(true); setEditingTaskForProject(null); }}>+</button>
                </div>
                {getStandaloneTasks().map(task => (
                  <div
                    key={task.id}
                    className={`task-item ${currentTaskId === task.id ? 'selected' : ''} ${task.completed ? 'completed' : ''}`}
                    onClick={() => !task.completed && handleTaskClick(task.id)}
                  >
                    <button
                      className={`complete-btn ${task.completed ? 'is-complete' : ''}`}
                      onClick={e => handleToggleComplete(e, task.id, task.completed)}
                      title={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
                    >
                      {task.completed ? '✓' : ''}
                    </button>
                    <div className="task-content">
                      {renamingTaskId === task.id ? (
                        <input
                          className="inline-rename-input"
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onBlur={() => commitRenameTask(task.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') commitRenameTask(task.id);
                            if (e.key === 'Escape') { setRenamingTaskId(null); setRenameValue(''); }
                          }}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                        />
                      ) : (
                        <span className="task-text" onDoubleClick={e => { e.stopPropagation(); startRenameTask(task.id, task.text); }}>{task.text}</span>
                      )}
                      <span className="task-time">{formatDuration(getDisplayTime(task.id, task.totalTimeSpent))}</span>
                    </div>
                    <button className="task-delete" onClick={e => { e.stopPropagation(); handleDeleteTask(task.id); }}>×</button>
                  </div>
                ))}
                {editingStandaloneTask && (
                  <div className="inline-task-input">
                    <div className="task-input-container">
                      <input
                        type="text"
                        className={`task-input ${taskError ? 'error' : ''}`}
                        placeholder="Enter task name. ENTER to save."
                        value={newTaskText}
                        onChange={e => { setNewTaskText(e.target.value); if (taskError) setTaskError(''); }}
                        onKeyDown={e => handleTaskKeyDown(e)}
                        autoFocus
                      />
                      <button className="task-input-cancel" onClick={() => { setEditingStandaloneTask(false); setNewTaskText(''); setTaskError(''); }}>×</button>
                    </div>
                    {taskError && <div className="error-message">{taskError}</div>}
                  </div>
                )}
              </div>
            )}

            {/* Add standalone task button when no standalone tasks exist yet */}
            {getStandaloneTasks().length === 0 && !editingStandaloneTask && (
              <button className="add-standalone-task-btn" onClick={() => setEditingStandaloneTask(true)}>
                + Add Task
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TaskPanel;
